import { Capacitor } from '@capacitor/core';
import { VxlBatchDownloader } from '@/plugins/vxlBatchDownloader';

// Camada fina entre o store Vuex e o bridge nativo. Toda comunicação com o
// `KeyVault.saveLease/loadLease/deleteLease` passa por aqui — facilita testes
// (mockando este módulo) e isola tratamento de erros do bridge (que tem
// reject custom format do Capacitor).
//
// IMPORTANTE: a fonte de verdade pra segurança é o arquivo nativo. O state
// Vuex é só cache pra reatividade do Vue (getters em template). O scheme
// handler vxlkey:// lê DIRETO do arquivo nativo, não do Vuex — então qualquer
// edição maliciosa via localStorage não afeta o gate de playback.

// Defaults Hotmart-style: 30 dias de validade + 3 dias de grace.
// Override por env var (VUE_APP_OFFLINE_LEASE_TTL_DAYS / GRACE_DAYS) e, no
// futuro, por enroll/produtor via campo `enroll.offlineLease`.
const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_TTL_DAYS = 30;
const DEFAULT_GRACE_DAYS = 3;

export function getDefaultConfig () {
  const ttlDays = Number(process.env.VUE_APP_OFFLINE_LEASE_TTL_DAYS) || DEFAULT_TTL_DAYS;
  const graceDays = Number(process.env.VUE_APP_OFFLINE_LEASE_GRACE_DAYS) || DEFAULT_GRACE_DAYS;
  return {
    ttlMs: ttlDays * DAY_MS,
    graceMs: graceDays * DAY_MS
  };
}

export function isLeaseFeatureEnabled () {
  // Kill switch global. Default true. Permite mergear gradualmente sem rebuild
  // nativo: env=false faz playableState sempre retornar 'valid'.
  return process.env.VUE_APP_OFFLINE_LEASE_ENABLED !== 'false';
}

// Cria um claim novo. Não persiste; só monta o payload.
export function buildClaim ({ enrollId, studentId, now, ttlMs, graceMs }) {
  const issuedAt = now;
  const validUntil = issuedAt + ttlMs;
  const graceUntil = validUntil + graceMs;
  return {
    v: 1,
    enrollId: String(enrollId),
    studentId: String(studentId),
    issuedAt,
    validUntil,
    graceUntil,
    ttlMs,
    graceMs,
    revokedAt: null
  };
}

// Classifica um claim em um dos 5 estados. Função pura, fácil de testar.
//
//   'valid'    → dentro do TTL, sem revogação
//   'grace'    → passou do validUntil mas ainda no grace period
//   'expired'  → passou do graceUntil OU studentId não bate
//   'revoked'  → backend marcou como revogado (resposta 402)
//   'unknown'  → sem claim (download pré-feature ou arquivo nativo ausente)
export function evaluate (claim, currentStudentId, now = Date.now()) {
  if (!claim) return 'unknown';
  if (claim.revokedAt != null) return 'revoked';
  // studentId bind: lease só é válido pra conta que estava logada quando foi
  // emitido. Trocou de conta → bloqueio. Re-login na conta original libera
  // (claim antigo ainda tá no disco e bate de novo).
  if (currentStudentId && String(claim.studentId) !== String(currentStudentId)) {
    return 'expired';
  }
  if (typeof claim.graceUntil === 'number' && now > claim.graceUntil) return 'expired';
  if (typeof claim.validUntil === 'number' && now > claim.validUntil) return 'grace';
  return 'valid';
}

// Quantos dias inteiros faltam até o graceUntil. Negativo se já passou.
// Usado pelo banner amarelo (≤3 dias) e pela mensagem do modal de bloqueio.
export function daysUntilExpiry (claim, now = Date.now()) {
  if (!claim || typeof claim.graceUntil !== 'number') return null;
  const diffMs = claim.graceUntil - now;
  return Math.ceil(diffMs / DAY_MS);
}

// ---------- Bridge wrappers ----------

function isNative () {
  return Capacitor.isNativePlatform();
}

export async function getLease (enrollId) {
  if (!isNative()) return null;
  try {
    const result = await VxlBatchDownloader.getLease({ enrollId: String(enrollId) });
    if (!result || !result.exists) return null;
    if (!result.valid) {
      // HMAC não bate — arquivo adulterado ou re-instalação que perdeu a chave
      // HMAC do Keystore. Apaga pra forçar regeneração no próximo touch online.
      try { await VxlBatchDownloader.removeLease({ enrollId: String(enrollId) }); } catch {}
      return null;
    }
    return result.claim || null;
  } catch (e) {
    console.warn('[lease] getLease falhou:', e?.message || e);
    return null;
  }
}

export async function setLease (enrollId, claim) {
  if (!isNative()) return false;
  try {
    await VxlBatchDownloader.setLease({ enrollId: String(enrollId), claim });
    return true;
  } catch (e) {
    console.warn('[lease] setLease falhou:', e?.message || e);
    return false;
  }
}

export async function removeLease (enrollId) {
  if (!isNative()) return;
  try {
    await VxlBatchDownloader.removeLease({ enrollId: String(enrollId) });
  } catch (e) {
    console.warn('[lease] removeLease falhou:', e?.message || e);
  }
}

export async function setActiveStudent (studentId) {
  if (!isNative()) return;
  try {
    await VxlBatchDownloader.setActiveStudent({ studentId: studentId == null ? '' : String(studentId) });
  } catch (e) {
    console.warn('[lease] setActiveStudent falhou:', e?.message || e);
  }
}
